import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useLanguage, useTranslation } from '../i18n';
import type { Task } from '../types';
import './TasksPage.css';

export const TasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = useTranslation();

  // Reload tasks when language changes
  useEffect(() => {
    loadTasks();
  }, [language]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getTasks();
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalCompleted = tasks.filter(t => t.completed).length;
  const totalCrystalsEarned = tasks.reduce((sum, t) => t.completed ? sum + t.reward : sum, 0);

  if (loading) {
    return (
      <div className="page-container tasks-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t.tasksPage.loadingTasks}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container tasks-page">
      <div className="page-heading-section">
        <h1 className="page-title">{t.tasksPage.title}</h1>
        <p className="page-subtitle">{t.tasksPage.subtitle}</p>
      </div>

      <div className="achievements-section">
        <h2 className="section-title">{t.tasksPage.achievements}</h2>
        <div className="achievements-grid">
          <div className="achievement-badge">
            <div className="achievement-icon">🌟</div>
            <div className="achievement-name">{t.tasksPage.newbie}</div>
          </div>
          <div className="achievement-badge locked">
            <div className="achievement-icon">🔥</div>
            <div className="achievement-name">{t.tasksPage.week}</div>
          </div>
          <div className="achievement-badge locked">
            <div className="achievement-icon">🏆</div>
            <div className="achievement-name">{t.tasksPage.month}</div>
          </div>
          <div className="achievement-badge locked">
            <div className="achievement-icon">💎</div>
            <div className="achievement-name">{t.tasksPage.expert}</div>
          </div>
        </div>
      </div>

      <div className="tasks-stats">
        <div className="stat-card">
          <div className="stat-icon">✓</div>
          <div className="stat-info">
            <div className="stat-value">{totalCompleted}/{tasks.length}</div>
            <div className="stat-label">{t.tasksPage.completed}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💎</div>
          <div className="stat-info">
            <div className="stat-value">{totalCrystalsEarned}</div>
            <div className="stat-label">{t.tasksPage.earned}</div>
          </div>
        </div>
      </div>

      <div className="tasks-section">
        <h2 className="section-title">{t.tasksPage.allTasks}</h2>
        <div className="tasks-list">
          {tasks.map(task => (
            <TaskCard 
              key={task.id} 
              task={task}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface TaskCardProps {
  task: Task;
}

const TaskCard: React.FC<TaskCardProps> = ({ task }) => {
  return (
    <div className={`task-card ${task.completed ? 'completed' : ''}`}>
      <div className="task-emoji-container">
        <div className="task-emoji">{task.emoji || '🎯'}</div>
        {task.completed && (
          <div className="task-completed-overlay">
            <span>✓</span>
          </div>
        )}
      </div>
      <div className="task-info">
        <div className="task-header">
          <h3 className="task-title">{task.title}</h3>
          <div className="task-reward">
            <span className="reward-icon">💎</span>
            <span className="reward-value">+{task.reward}</span>
          </div>
        </div>
        <p className="task-description">{task.description}</p>
        {task.total !== undefined && task.total !== null && (
          <div className="task-progress">
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${Math.min((task.progress || 0) / task.total * 100, 100)}%` }}
              />
            </div>
            <span className="progress-text">{task.progress || 0}/{task.total}</span>
          </div>
        )}
      </div>
    </div>
  );
};
